import { useState } from "react";
import { Button } from "@/components/ui/button";
import { MapPin, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

interface Props {
  leadId: string;
  disabled?: boolean;
}

/**
 * One-click normaliser: for every itinerary item on this lead that has a
 * supplier name, look up the supplier's city/territory and replace
 * `location` with `city ?? territory` when the current value is empty or
 * equals the supplier's country (i.e. was auto-populated from territory
 * before we started preferring city). Manual overrides are preserved.
 */
export function NormaliseLocationsButton({ leadId, disabled }: Props) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);

  const handleClick = async () => {
    setBusy(true);
    try {
      const { data: count, error } = await (supabase as any).rpc("normalise_lead_item_locations", { _lead_id: leadId });
      if (error) throw error;
      const updates = { length: Number(count) || 0 };
      if (updates.length === 0) {
        toast.info("Locations already look consistent");
        return;
      }

      await qc.invalidateQueries({ queryKey: ["itinerary-items"] });
      await qc.invalidateQueries({ queryKey: ["lead", leadId] });
      toast.success(`Updated ${updates.length} item${updates.length === 1 ? "" : "s"}`);
    } catch (err: any) {
      console.error("Normalise locations failed:", err);
      toast.error(err?.message || "Failed to normalise locations");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      onClick={handleClick}
      variant="outline"
      size="sm"
      disabled={disabled || busy}
      title="Fill blank or country-only locations from the supplier city, the hotel stay on that date, or the programme cities"
    >
      {busy ? (
        <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
      ) : (
        <MapPin className="h-4 w-4 mr-1.5" />
      )}
      Normalise locations
    </Button>
  );
}
